/**
 * What a step's status is, given the API phase behind it.
 *
 * Split out of `deriveStepProgress` so it can be asserted — `usePhases` reaches
 * the API client and cannot be imported by a test, which is why the bug below
 * went unnoticed.
 *
 * The bug: several steps share one phase row. `outcome` borrows the luteal
 * phase's so the wait can read its dates, and status came along with them.
 * Skipping luteal support writes `startDate === endDate`, which is how this
 * codebase encodes *skipped* — so telling the app you are not taking
 * progesterone also declared your result skipped, before any result existed.
 * Completing luteal support did the same in reverse.
 */
import {
  isCycleStepComplete,
  type PhaseStepCompletionSource,
} from "./phaseStepCompletion";

export type DerivedStepStatus =
  "pending" | "completed" | "skipped" | "scheduled";

/**
 * Completion reads more than the dates — a trigger shot, a PGT result — so this
 * is the completion source plus the two dates that decide skipped and scheduled.
 */
export type StepPhaseSource = PhaseStepCompletionSource & {
  startDate?: string | null;
};

/**
 * Whether the patient has finished the steps that are hers to finish.
 *
 * A derived step is excluded, and that exclusion is the whole point. The outcome
 * step is permanently pending — nothing she does completes it, only a result
 * does — so counting it would make "all steps complete" unreachable for every
 * flow that ends in a result, and the celebration that depends on it would never
 * appear on the cycles it was written for.
 *
 * A step with no progress row at all still blocks. Absent is not finished.
 */
export function patientStepsAreComplete(
  steps: readonly { id: string; derivedProgress?: boolean }[],
  progressByStepId: (stepId: string) => { status?: string } | undefined,
): boolean {
  const hers = steps.filter((step) => !step.derivedProgress);
  if (hers.length === 0) return false;

  return hers.every((step) => {
    const status = progressByStepId(step.id)?.status;
    return status === "completed" || status === "skipped";
  });
}

export function resolveStepProgressStatus(params: {
  stepId: string;
  /** True when the cycle decides this step, not the patient and not a phase. */
  derivedProgress?: boolean;
  stepIds: Parameters<typeof isCycleStepComplete>[1];
  phase: StepPhaseSource | null | undefined;
  todayIso: string;
}): { status: DerivedStepStatus; scheduledFor: string | null } {
  const { stepId, derivedProgress, stepIds, phase, todayIso } = params;

  // Takes nothing from the phase it borrows — not its skip, not its completion.
  if (derivedProgress) return { status: "pending", scheduledFor: null };

  if (!phase) return { status: "pending", scheduledFor: null };

  const isSkipped =
    !!phase.startDate && !!phase.endDate && phase.startDate === phase.endDate;
  if (isSkipped) return { status: "skipped", scheduledFor: null };

  if (isCycleStepComplete(stepId, stepIds, phase, todayIso)) {
    return { status: "completed", scheduledFor: null };
  }

  // A phase with a `startDate` and no `endDate` was scheduled by the patient or
  // the clinic. The hub renders that as its own card; the countdown-vs-"due to
  // log" distinction is made downstream by comparing `scheduledFor` with today.
  if (phase.startDate && !phase.endDate) {
    return { status: "scheduled", scheduledFor: phase.startDate ?? null };
  }

  return { status: "pending", scheduledFor: null };
}
